import { useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Stack,
  Typography,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import PermissionLevelTooltip from "../permissions/PermissionLevelTooltip";
import { presets, getPresetFromActions } from "../../permissions/permissionConfig";
import { actionsToLevel } from "../../permissions/permissionsMapper";

export default function PermissionsCategoryAccordion({
  title,
  subTitle,
  resources,
  value,
  onChange,
  defaultExpanded = false,
  showGrantedCount = true,
  permissionLevelDefinitions = {},
  lockedLevelByKey = []
}) {
  const levels = value ?? {};

  const isLockedKey = (lockedLevelByKey, key) =>
    !!lockedLevelByKey && Object.prototype.hasOwnProperty.call(lockedLevelByKey, key);

  const setLevels = useCallback(
    (updater) => {
      if (!onChange) return;
      onChange(updater);
    },
    [onChange]
  );

  // Convert legacy "admin"/"none" to actions array format
  const getActionsForResource = useCallback((key) => {
    const val = levels[key];
    if (!val || val === "none") return [];
    if (val === "admin") return ["view", "edit", "delete", "admin"];
    if (Array.isArray(val)) return val;
    return [];
  }, [levels]);

  // Set actions for a resource
  const setActionsForResource = useCallback((key, actions) => {
    if (isLockedKey(lockedLevelByKey, key)) return;
    setLevels((prev) => ({
      ...(prev ?? {}),
      [key]: actionsToLevel(actions)
    }));
  }, [setLevels, lockedLevelByKey]);

  // Handle preset change
  const handlePresetChange = useCallback((key, presetId) => {
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      setActionsForResource(key, preset.actions);
    }
  }, [setActionsForResource]);

  const grantedCount = useMemo(() => {
    return resources.reduce((acc, r) => {
      const locked = isLockedKey(lockedLevelByKey, r.key);
      const actions = locked
        ? ["view", "edit", "delete", "admin"]
        : getActionsForResource(r.key);
      return acc + (actions.length > 0 ? 1 : 0);
    }, 0);
  }, [levels, resources, lockedLevelByKey, getActionsForResource]);

  const applyBulkPreset = useCallback((presetId) => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return;
    setLevels((prev) => {
      const next = { ...(prev ?? {}) };
      for (const r of resources) {
        if (isLockedKey(lockedLevelByKey, r.key)) continue;
        next[r.key] = actionsToLevel(preset.actions);
      }
      return next;
    });
  }, [resources, setLevels, lockedLevelByKey]);

  const handleGrantAll = useCallback(() => applyBulkPreset("admin"), [applyBulkPreset]);
  const handleRevokeAll = useCallback(() => applyBulkPreset("none"), [applyBulkPreset]);

  const allGranted = grantedCount === resources.length;
  const noneGranted = grantedCount === 0;

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      square
      sx={{
        borderRadius: 0,
        border: "none",
        borderBottom: "1px solid",
        borderBottomColor: grantedCount > 0 ? alpha("#4caf50", 0.3) : "#e0e0e0",
        backgroundColor: grantedCount > 0 ? alpha("#4caf50", 0.02) : "transparent",
        boxShadow: "none",
        "&:before": { display: "none" },
        "&.Mui-expanded": {
          backgroundColor: grantedCount > 0 ? alpha("#4caf50", 0.02) : "transparent",
          boxShadow: "none",
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          py: 1,
          minHeight: 56,
          backgroundColor: grantedCount > 0 ? alpha("#4caf50", 0.02) : "transparent",
          "&:hover": { backgroundColor: grantedCount > 0 ? alpha("#4caf50", 0.04) : "rgba(0,0,0,0.015)" },
          "& .MuiAccordionSummary-content": { margin: 0 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {title}
              </Typography>
              {showGrantedCount && (
                <Chip
                  size="small"
                  label={`${grantedCount}/${resources.length}`}
                  color={allGranted ? "success" : noneGranted ? "default" : "warning"}
                  variant={noneGranted ? "outlined" : "filled"}
                  sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }}
                />
              )}
              <PermissionLevelTooltip permissionLevelDefinitions={permissionLevelDefinitions} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subTitle}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
          >
            <Button
              size="small"
              variant="text"
              color="success"
              onClick={handleGrantAll}
              disabled={allGranted}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Grant all
            </Button>
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={handleRevokeAll}
              disabled={noneGranted}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Revoke all
            </Button>
          </Stack>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 130, fontWeight: 500, color: "text.secondary", fontSize: "0.8rem", borderBottomColor: "#eee" }}>Access Level</TableCell>
              <TableCell sx={{ fontWeight: 500, color: "text.secondary", fontSize: "0.8rem", borderBottomColor: "#eee" }}>Page</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map((r) => {
              const locked = isLockedKey(lockedLevelByKey, r.key);
              const currentActions = locked
                ? ["view", "edit", "delete", "admin"]
                : getActionsForResource(r.key);
              const currentPreset = getPresetFromActions(currentActions);
              const isGranted = currentActions.length > 0;

              return (
                <TableRow
                  key={r.key}
                  sx={{
                    backgroundColor: isGranted ? alpha("#4caf50", 0.04) : 'transparent',
                    '&:hover': {
                      backgroundColor: isGranted ? alpha("#4caf50", 0.08) : alpha("#000", 0.02)
                    }
                  }}
                >
                  {/* Access Level - Left column, fixed width */}
                  <TableCell sx={{ width: 130 }}>
                    {locked ? (
                      <Chip
                        size="small"
                        label="Always On"
                        color="info"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', height: 28 }}
                      />
                    ) : (
                      <Select
                        size="small"
                        value={currentPreset.id}
                        onChange={(e) => handlePresetChange(r.key, e.target.value)}
                        sx={{
                          minWidth: 100,
                          fontSize: '0.85rem',
                          '& .MuiSelect-select': { py: 0.75, px: 1.5 }
                        }}
                      >
                        {presets.map((preset) => (
                          <MenuItem key={preset.id} value={preset.id}>
                            {preset.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </TableCell>

                  {/* Page - Right column */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {isGranted ? (
                        <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      ) : (
                        <BlockIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isGranted ? 500 : 400,
                          color: isGranted ? 'text.primary' : 'text.secondary'
                        }}
                      >
                        {r.label}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}